import { useEffect, useState } from 'react';

import {
  styled,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

import type { FormEvent } from 'react';
import type { SxProps } from '@mui/material';

import { useTranslation } from 'react-i18next';
import { loginAtom, tokenAtom } from '@/interfaces/auth';
import { useAtom, useSetAtom } from 'jotai';
import { getErrInfo } from '../common/utils';
import { autoLoginAtom } from '@/interfaces/utils';

interface LoginFormProps {
  width: number;
  height: number;
  buttonStyle: SxProps;
}

const LoginInput = styled(TextField)({
  width: 300,
  '& div.MuiInput-underline:before': { borderBottomColor: 'white' },
  '& div.MuiInput-underline:hover:before': { borderBottomColor: 'white' },
  '& div.MuiInput-underline.Mui-focused:before': { borderBottomColor: 'white' },
  '& div.MuiInput-underline:after': { borderBottomColor: 'white' },
  '& div.MuiInput-underline.Mui-focused:after': { borderBottomColor: 'white' },
  '& label': { color: 'white' },
  '& label.Mui-focused': { color: 'white' },
  '& input': { color: 'white' },
});

export default function LoginForm({ width, height, buttonStyle }: LoginFormProps) {
  const { t } = useTranslation();

  const [{ data: loginData, mutate, status, error }] = useAtom(loginAtom);
  const setTokenInfo = useSetAtom(tokenAtom);
  const [autoLogin, setAutoLogin] = useAtom(autoLoginAtom);

  const [showPassword, setShowPassword] = useState(false);
  const [autoCheck, setAutoCheck] = useState(autoLogin);

  useEffect(() => {
    if (status && status === 'success' && loginData) {
      setAutoLogin(autoCheck);
      setTokenInfo({
        accessToken: loginData.accessToken,
        refreshToken: loginData.refreshToken,
      });
    } else if (status === 'error' && error) {
      console.log(error);
      const errInfo = getErrInfo(error);
      let errMsgKey = errInfo.headerCode ? 'Common.message.serverErrorOccurred' : 'Common.message.unknownErrorOccurred';
      switch (errInfo.bodyCode) {
        case 'BAD_REQUEST':
        case 'USER_NOT_FOUND':
          errMsgKey = 'Login.message.invalidEmailOrPassword';
          break;
        default:
          errMsgKey = errInfo.commonMsgKey !== null && errInfo.commonMsgKey;
          break;
      }
      alert(t(errMsgKey));
    }
  }, [autoCheck, error, loginData, setAutoLogin, setTokenInfo, status, t]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const data = new FormData(event.currentTarget);
    const param = {
      email: data.get('email'),
      password: data.get('password'),
    };

    mutate(param);
  }

  const toggleVisibility = () => setShowPassword(!showPassword);
  const ShowPasswordIcon = (
    <InputAdornment position="end">
      <IconButton onClick={toggleVisibility}>{showPassword ? <Visibility /> : <VisibilityOff />}</IconButton>
    </InputAdornment>
  );

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit}
      alignItems="center"
      justifyContent="center"
      sx={{
        width,
        height,
        borderRadius: '0 35px 35px 0',
        bgcolor: '#324468',
        '& *': { color: 'white' },
      }}
    >
      <LoginInput name="email" label={t('Login.label.emailField')} variant="standard" />
      <LoginInput
        name="password"
        label={t('Login.label.passwordField')}
        variant="standard"
        type={showPassword ? 'text' : 'password'}
        InputProps={{ endAdornment: ShowPasswordIcon }}
        sx={{ mt: 3 }}
      />
      <FormControlLabel
        name="autoLogin"
        control={<Checkbox onChange={(e) => setAutoCheck(e.target.checked)} checked={autoCheck} />}
        label={t('Login.label.autoLogin')}
        sx={{ width: 300, mt: 1 }}
      />
      <Button
        type="submit"
        sx={{
          ...buttonStyle,
          bgcolor: '#2D3748',
          color: 'white',
        }}
      >
        {t('Login.label.loginButton')}
      </Button>
      <Typography fontSize={14} sx={{ mt: 3 }}>
        * {t('Login.description.termOfUseInfoLine1')}
        <br />
        {t('Login.description.termOfUseInfoLine2')}
      </Typography>
    </Stack>
  );
}
